//Library Imports
import React, { useState, useEffect } from "react";
import store, { RootState } from "store";
import { Spin, Tooltip, Typography } from "antd";
import { Link, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";

//Local Imports
import EditIcon from "../../../assets/img/icons/Edit.svg";
import Input from "../../Common/Components/Input";
import TickNormal from "../../../assets/img/icons/tickNormal.svg";
import crossNormal from "../../../assets/img/icons/crossNormal.svg";
import { ReactComponent as Flow } from "../../../assets/img/workflow_icn.svg";
import { ReactComponent as BackArrow } from "../../../assets/img/icons/Back_Arrow.svg";
import { getQueryParam, removeOnClickBubble } from "utils";
import { AUDIENCE_LISTS_DETAILS, CAMPAIGN_SENT_EDITIONS } from "constants/types";
import { updateAssetAction } from "modules/Campaigns/Emails/actions";
import { setListDetailsNameError } from "modules/Campaigns/Audience/Lists/actions";
import classNames from "classnames";

//Object Contains Style for text styling
const styleForName = {
  fontFamily: "roboto_slabregular, sans-serif",
  fontSize: "18px",
  color: "#000000",
  margin: 0,
  padding: 0,
  fontWeight: "bold",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const styleForNameSm = {
  ...styleForName,
  fontWeight: "normal",
  fontSize: "13px",
  maxWidth: "160px",
};

// Object Contains Style for image styling
const styleForPencil = {
  height: "13px",
  width: "13px",
  marginBottom: "11px",
};
const styleForPencilSm = {
  height: "11px",
  width: "11px",
  marginBottom: "9px",
};

//Object contains style that need to be applied to the cross/cancel button
const styleForSaveAndCancel = { maxWidth: "15px" };

//Object Contains Style  for input field styling
const styleFotInputFieldContainer = { marginLeft: "0px" };

const styleForNameEditInputField = { width: "165px" };
const styleForNameEditInputFieldSm = { width: "135px" };

interface EditItemNameProps {
  itemName?: string;
  itemTitle?: string;
  showCircle?: boolean;
  nonEditable?: boolean;
  hideBack?: boolean;
  editionStatus?: String;
  assetId?: number;
  campaignName?: string
  workflowName?: string
  workflowId?: number
  tab?: string;
  page?: string;
  submitHandler?: (value: any) => void;
  fieldName?: string;
  loading?: boolean;
  itemNameLoading?: boolean;
  cloned?: boolean;
  isSmallScreen?: boolean;
  useExternalStyleObjects?: boolean;
  placement?: string;
  setIsEditing?: any;
  goBackURL?: string;
  tooltipPlacement?: string;
  goBackFunction?: any;
  commasNotAllowed?: boolean;
  disableBackButton?: boolean;
  errorClassName?: string;
  errorMessage?: string;
  onReset?: any;
  emailId?: number;
  isDigestFlag?: boolean
}
const EditItemName: React.FC<EditItemNameProps> = ({
  itemName,
  itemTitle,
  showCircle,
  nonEditable,
  hideBack,
  editionStatus,
  submitHandler,
  assetId,
  campaignName,
  workflowName,
  workflowId,
  fieldName,
  loading,
  cloned,
  isSmallScreen,
  useExternalStyleObjects,
  placement,
  setIsEditing,
  goBackURL,
  tooltipPlacement,
  goBackFunction,
  commasNotAllowed,
  disableBackButton,
  errorClassName,
  errorMessage,
  onReset,
  emailId,
  tab,
  isDigestFlag
}) => {
  const EditTooltip = <span>{"Edit"}</span>;

  const history = useHistory();
  const { Paragraph } = Typography;
  const [toggleEditMode, setToggleEditMode]: [any, any] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [inputValue, setInputValue] = useState(itemName);
  const [ellipsisState, setEllipsisState] = useState(false);
  const [campaignEllipsisState, setCampaignEllipsisState] = useState(false);
  const [emptyError, setEmptyError] = useState(false);
  const [commasError, setCommasError] = useState("");
  const audienceListDetailsMessage = useSelector((state: RootState) => state.audienceListDetails.message);
  const message = errorMessage ?? audienceListDetailsMessage;
  const successStatus = useSelector((state: RootState) => state.audienceLists.editListName.success);
  const [isDisable, setIsDisable] = useState(false);
  const { loadText, loading: saveDesignLoading } = useSelector((state: RootState) => state.design.saveDesign);

  // Redux States
  const workflowNameLoading: boolean = useSelector(
    (state: RootState) => state.automation.workflow.detail.nameLoading
  );
  const groupNameLoading: boolean = useSelector(
    (state: RootState) => state.salesXGroups.selectedGroup.nameLoading
  );

  let styleObject: any = {};

  if (useExternalStyleObjects) {
    styleObject = {
      paragraphStyle: isSmallScreen ? styleForName : styleForNameSm,
      saveAndCancelStyle: styleForSaveAndCancel,
      pencilStyle: isSmallScreen ? styleForPencil : styleForPencilSm,
      inputStyle: isSmallScreen ? styleForNameEditInputField : styleForNameEditInputFieldSm,
      inputContainerStyle: styleFotInputFieldContainer,
    };
  }

  useEffect(() => {
    if (workflowNameLoading || groupNameLoading) {
      setEditMode(true);
      setIsDisable(true);
    } else {
      setEditMode(false);
      setIsDisable(false);
    }
  }, [workflowNameLoading, groupNameLoading]);

  const setItemNameHandler = () => {
    if (submitHandler) {
      submitHandler(inputValue?.trim());
      if (window.location.pathname.includes("lists/view")) {
        setIsDisable(true);
      }
      // setEditMode(false);
      if (setIsEditing) {
        setIsEditing(false);
      }
      if (message) {
        setListDetailsNameError('');
      }
    } else {
      let asset_id: any;
      if (assetId) {
        asset_id = assetId;
      } else {
        asset_id = getQueryParam("asset_id");
      }
      let asset_name: any;
      if (inputValue !== undefined) {
        asset_name = inputValue;
      } else {
        asset_name = itemName;
      }
      updateAssetAction(asset_name, asset_id)
        .then((response) => {
          store.dispatch({
            type: CAMPAIGN_SENT_EDITIONS.SET_NAME,
            payload: asset_name,
          });
        })
        .catch((err) => {
          console.log("Asset Name Error : ", err);
        });
    }
  };

  const resetItemNameHandler = (e?: any) => {
    if (e) removeOnClickBubble(e);
    setEditMode(false);
    if (setIsEditing) setIsEditing(false);
    setInputValue(itemName);
    if (emptyError) setEmptyError(false);
    if (commasError) setCommasError("");
    if (message) {
      setListDetailsNameError('');
    }
    if (onReset) {
      onReset();
    }
  };

  const handleSave = (e?: any) => {
    removeOnClickBubble(e);
    if (itemName !== inputValue?.trim()) {
      if (inputValue?.trim() !== "") {
        setItemNameHandler();
        if (window.location.pathname.startsWith("/campaigns")) {
          setEditMode(false);
          if (setIsEditing) setIsEditing(false);
        }
      } else {
        setEmptyError(true);
      }
      if (commasError) setCommasError("");
    } else {
      setEditMode(false);
      if (setIsEditing) setIsEditing(false);
      resetItemNameHandler();
    }
  };

  const handleInputKeyUp = (e: any) => {
    if (e.key === "Enter") {
      if (itemName !== inputValue?.trim()) {
        if (inputValue?.trim() !== "") {
          setItemNameHandler();
          if (window.location.pathname.startsWith("/campaigns")) {
            setEditMode(false);
            if (setIsEditing) setIsEditing(false);
          }
        } else {
          setEmptyError(true);
        }
      } else {
        setEditMode(false);
        if (setIsEditing) setIsEditing(false);
        setToggleEditMode("editable-active");
        resetItemNameHandler();
      }
    }
  };

  useEffect(() => {
    setInputValue(itemName);
  }, [itemName]);

  useEffect(() => {
    if (successStatus) {
      setEditMode(false);
    }

    setIsDisable(false);
  }, [successStatus]);

  useEffect(() => {
    // when a user click on edit mode and click browser back button it will disable edit mode
    if (hideBack && editMode) {
      setEditMode(false);
    }
  }, [hideBack]);

  // when in edit mode, input change handler
  const hanldeOnChange = (e: any) => {
    removeOnClickBubble(e);
    let value = e.target.value;
    if (emptyError) setEmptyError(false);
    if (message) {
      setListDetailsNameError('');
    }
    if (isDisable) {
      setIsDisable(false);
    }
    if (commasNotAllowed && value.includes(",")) {
      setCommasError("',' is not allowed.");
    } else {
      if (commasError) setCommasError("");
      setInputValue(e.target.value);
    }
  };

  useEffect(() => {
    if (message) {
      setIsDisable(false);
    }
  }, [message]);

  const isEditModeEnable = (e: any) => {
    removeOnClickBubble(e);
    setEditMode(true);
    if (setIsEditing) setIsEditing(true);
    if (message) {
      setListDetailsNameError('');
    }
  };

  useEffect(() => {
    setInputValue(itemName);
  }, []);

  useEffect(() => {
    if (saveDesignLoading && loadText === "Saving changes" && window.location.pathname.startsWith("/campaigns") && editMode) {
      handleSave();
    }
  }, [saveDesignLoading])

  return (
    <EditItemNameWrapper id="editable-name-wrapper" className={classNames({ isDigestArea: isDigestFlag })}>
      {hideBack ? (
        ""
      ) : (
        <div className="d-flex flex-row bottom-margin">
          <a
            className={`${disableBackButton ? "disabled" : ""}`}
            disabled={disableBackButton}
            onClick={() => {
              if (editMode) setEditMode(false);
              if (goBackFunction) {
                goBackFunction();
                return;
              }
              if (goBackURL) {
                history.push(goBackURL);
                return;
              }
              history.location.pathname === "/campaigns/content/asset/"
                ? history.push(`/campaigns`)
                : history.goBack();
            }}
            id="header-back-btn"
          >
            <BackArrow className="top-margin cursor-pointer" />
          </a>
          <div className="verticalLine top-margin me-2"></div>
        </div>
      )}
      <div className="email-name-box">
        {itemTitle &&
          <div className="title-div">
            <div className="small-label">
              {itemTitle}
            </div>
            <div className="verticalLine small-verticalLine top-margin me-2"></div>
            <Link className="wrapper-link" to={`/campaigns/content?campaign_id=${assetId}`}>
              <Tooltip
                placement={"bottom"}
                title={campaignName}
              >
                <Paragraph
                  className="campaign-value pointer"
                >
                  <b>Campaign: </b>{campaignName}
                </Paragraph>
              </Tooltip>
            </Link>
            {workflowName && <><div className="verticalLine small-verticalLine top-margin me-2"></div>
              <Tooltip
                placement={"bottom"}
                title={workflowName}
              >
                <a href={`/automation/details?workflow_id=${workflowId}&campaign_id=${assetId}&email_id=${emailId}&from=emailPreview${tab !== undefined ? `&tab=${tab}` : ``}`}><Flow className="pointer" /></a>
              </Tooltip></>}

          </div>}
        <div className="">
          <div>{showCircle === true && <Circle>A</Circle>}</div>
          <div className="">
            <span className={`name d-flex name-container`}>
              <span className={`ellipsis type ${editMode ? "opacity-hidden" : ""}`}>
                <div className="hidden-div email-name" data-qa="qa-campign-name">
                  <>
                    {loading
                      ? ""
                      : cloned
                        ? "[Edited] Edition Draft"
                        : window.location.pathname.includes("editionedit")
                          ? editionStatus === "Sent"
                            ? `[Sent] ${itemName}`
                            : itemName
                          : itemName
                            ? itemName
                            : ""}
                  </>
                </div>
                <Tooltip
                  placement={placement ? placement : tooltipPlacement ? tooltipPlacement : "top"}
                  title={ellipsisState ? itemName : undefined}
                >
                  <Paragraph
                    className="email-name"
                    data-id="qa-list-header-name"
                    ellipsis={{
                      onEllipsis: setEllipsisState,
                    }}
                    {...(useExternalStyleObjects && { style: styleObject.paragraphStyle })} //External style being passed from props
                  >
                    <>
                      {loading
                        ? ""
                        : cloned
                          ? "[Edited] Edition Draft"
                          : window.location.pathname.includes("editionedit")
                            ? editionStatus === "Sent"
                              ? `[Sent] ${itemName}`
                              : itemName
                            : itemName
                              ? itemName
                              : ""}
                    </>
                  </Paragraph>
                </Tooltip>
              </span>
              <span
                className={`edit-button ${editMode === true || nonEditable ? "d-none" : ""}`}
                data-id="edit-button-pen-wrapper"
              >
                <span
                  className="edit-margin-top cursor-pointer d-flex"
                  data-id="edit-button-pen"
                  onClick={isEditModeEnable}
                >
                  <Tooltip placement="top" title={EditTooltip}>
                    <img
                      src={EditIcon}
                      alt="edit"
                      data-id="edit-button-pen-img"
                      {...(useExternalStyleObjects && { style: styleObject.pencilStyle })} //External style being passed from props
                    />
                  </Tooltip>
                </span>
              </span>

              {editMode ? (
                <InputWrapper
                  className={isDisable ? "disabled" : ""}
                  {...(useExternalStyleObjects && { style: styleObject.inputContainerStyle })}
                >
                  <Input
                    type="text"
                    value={inputValue}
                    onChange={hanldeOnChange}
                    onFocus={(e: any) => {
                      removeOnClickBubble(e);
                    }}
                    onKeyPress={handleInputKeyUp}
                    errorClassName={errorClassName}
                    error={
                      message
                        ? message
                        : emptyError
                          ? `${fieldName || "This Field"} cannot be empty.`
                          : commasError
                            ? commasError
                            : null
                    }
                    {...(useExternalStyleObjects && { style: styleObject.inputStyle })} //External style being passed from props
                    idInput="editable-input"
                  />
                  <span
                    onClick={handleSave}
                    className={`save ${isDisable || message?.length > 0 ? "cdisable" : ""}`} // message.length > 0 diables Tick if Error msg exists 
                    data-qa="qa-save-name"
                  >
                    <img
                      src={TickNormal}
                      alt="save"
                      {...(useExternalStyleObjects && { style: styleObject.saveAndCancelStyle })}
                    />
                  </span>
                  <span
                    onClick={resetItemNameHandler}
                    className={`cancel ${isDisable ? "cdisable" : ""}`}
                    data-qa="qa-cancel-name"
                  >
                    <img
                      src={crossNormal}
                      alt="cancel"
                      {...(useExternalStyleObjects && { style: styleObject.saveAndCancelStyle })}
                    />
                  </span>
                </InputWrapper>
              ) : null}
            </span>
          </div>
        </div>
        {window.location.pathname.includes("lists/view") ||
          (window.location.pathname.startsWith("/audiences/people") && message !== "" && (
            <span className="error-msg-span">{message}</span>
          ))}
      </div>
    </EditItemNameWrapper>
  );
};

export default EditItemName;

const Circle = styled.span`
  margin-left: 0.5em;
  color: #ffffff;
  font-weight: 700;
  font-size: 24px;
  background: #27ce0a;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EditItemNameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  .error-msg-span {
    color: #db3725;
    font-style: italic;
    font-size: 14px;
    text-align: left;
    float: left;
    margin-top: 1px;
    margin-right: 5px;
    font-family: "robotomedium_italic";
    font-weight: normal;
    margin-bottom: -24px;
  }
  .bottom-margin {
    margin: auto 0;
    svg {
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 20px;
        height: auto;
      }
    }
  }
  .disabled {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.5;
  }
  .edit-button {
    padding-left: 10px;
    cursor: pointer;
  }
  .name-container {
    position: relative;
    margin-right: 10px;
    width: 100%;
    .opacity-hidden {
      opacity: 0;
    }
  }
  /* span.type {
    max-width: calc(100% - 60px);
  } */
  .email-name-box {
    width: calc(100% - 70px);
    .small-label {
      color: #27ce0a;
      font-size: 14px;
      font-family: "Roboto Slab", serif;
      font-weight: 700;
    }
    .title-div{
      display: flex;
      align-items: center;
      margin-top: -16px;
      max-width: 100%;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .email-name {
      color: #424b50;
      font-size: 40px;
      font-weight: 600;
      line-height: 49px;
      margin-bottom: 0px;
      margin-left: 0px;
      /* max-width: 350px; */
      font-family: "Roboto Slab", serif;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 28px;
        line-height: 33px;
      }
      &.hidden-div {
        opacity: 0;
        height: 1px;
        overflow: hidden;
        margin-right: -50px;
        margin-top: -1px;
        padding-right: 50px;
      }
    }
    .pointer{
      cursor: pointer;
    }
    .campaign-name {
      color: #67696A;
      font-size: 12px;
      font-weight: 700;
      line-height: 15px;
      margin-bottom: 0px;
      margin-left: 0px;
      max-width: 206px;
      font-family: "Roboto Slab", serif;
    }
    .wrapper-link {
      max-width: 100%;
      /* @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 75px;
      }  */
    }
    .campaign-value{
      font-size: 12px;
      line-height: 15px;
      margin-bottom: 0px;
      margin-left: 0px;
      /* max-width: 206px; */
      font-family: "Roboto Slab", serif;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #979797;
      font-weight: 500;
      width: 100%;
      b {
        color: #67696A;
        font-size: 12px;
        font-weight: 700;
        line-height: 15px;
        margin-bottom: 0px;
        margin-left: 0px;
        max-width: 206px;
        font-family: "Roboto Slab", serif;
      }
    }
  }
  .email-name-section {
    margin-right: 20px;
    margin-right: 25px;
  }
  .verticalLine {
    height: 24px;
    border: 1px solid #dddddd;
    width: 1px;
    padding: 0;
    background: #dddddd;
    margin-left: 11px;
  }
  .small-verticalLine {
    height: 18px !important;
    margin-left: 5px;
  }
  .edit-margin-top {
    line-height: 42px;
    margin-left: 10px;
    height: 100%;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      line-height: 30px;
    }
    img {
      vertical-align: bottom;
    }
  }
  .cursor-pointer {
    cursor: pointer;
  }
  .editable-active {
    .editable {
      display: block;
    }
  }
  .paragraph-styling {
    margin: 1em 0 1.5em 0;
    color: #7e7e7e;
    font-size: 16px;
    font-weight: 500;
    font-family: "robotomedium";
    line-height: 1.25;
    text-align: justify;
    text-justify: inter-word;
  }
`;
const InputWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  margin-left: 10px;

  label {
    position: absolute;
    right: 0;
    top: -25px;
    margin: 0;
  }
  input {
    padding-right: 70px;
    width: 440px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: 325px;
    }
  }
  .save {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      top: 5px;
    }
    img {
      vertical-align: middle;
      max-width: 20px;
    }
  }
  .cancel {
    position: absolute;
    top: 10px;
    right: 40px;
    cursor: pointer;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      top: 5px;
    }
    img {
      vertical-align: middle;
      max-width: 20px;
      padding: 2px;
    }
  }
  .cdisable {
    pointer-events: none;
    opacity: 0.5;
  }
`;
